"use client";
import { Typography } from "@/components/Typography/Typography.component";
import { useDotButton } from "@/hooks/useDotButton/useDotButton";
import { cn } from "@/lib/utils";
import { EmblaCarouselType } from "embla-carousel";
import Fade from "embla-carousel-fade";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { PullQuotePropsModel } from "./PullQuote.model";
import {
  DotButton,
  NextButton,
  PrevButton,
} from "./PullQuoteCarousel/PullQuoteCarousel.component";

export function PullQuote({ itemsCollection: items }: PullQuotePropsModel) {
  const [carouselRef, api] = useEmblaCarousel({ align: "start" }, [Fade()]);
  const [canScrollPrev, setCanScrollPrev] = useState(true);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  // onSelect useEffect
  {
    useEffect(() => {
      if (!api) {
        return;
      }
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);
  }

  const singleQuote = items.length === 1;

  return (
    <div className="bg-core-white w-full overflow-hidden px-4 py-30 md:px-8 lg:m-auto lg:max-w-[50%]">
      <div ref={carouselRef} className="overflow-hidden">
        <div className={cn("flex gap-8")}>
          {items.map((item, index) => (
            <div
              key={item.sys?.id || index}
              className={cn(
                "flex flex-none basis-full flex-col gap-y-20 lg:gap-y-10",
              )}
            >
              <Typography.H3Sentence
                className={cn("multiple-items !normal-case")}
              >
                &quot;{item.quote}&quot;
              </Typography.H3Sentence>
              <div className={cn("grid")}>
                <Typography.LLABEL1>
                  - {item.clientName}, {item.jobTitle}
                </Typography.LLABEL1>
                <Typography.LLABEL2>{item.company}</Typography.LLABEL2>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className={cn(
          "mt-20 flex place-content-between",
          singleQuote && "hidden",
        )}
      >
        <div className="inline-flex gap-4">
          {scrollSnaps.map((_: any, index: number) => (
            <DotButton
              key={index}
              aria-label={`quote-dot-${index + 1}`}
              onClick={() => onDotButtonClick(index)}
              className={cn(index === selectedIndex && "bg-black")}
            />
          ))}
        </div>
        <div className="flex justify-end gap-8">
          <PrevButton onClick={scrollPrev} canScrollPrev={canScrollPrev} />
          <NextButton onClick={scrollNext} canScrollNext={canScrollNext} />
        </div>
      </div>
    </div>
  );
}
